import { useLayoutEffect } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';

/**
 * PSTUDIO-4217. Puts the document back at the top when the user navigates
 * FORWARD into a new route.
 *
 * The whole app scrolls the DOCUMENT — `.c-page` is `min-height: 100dvh` in
 * normal flow, so `<html>` is the only scroll container — and react-router
 * does nothing to the scroll offset on a client-side navigation. Every route
 * was therefore entered at the PREVIOUS route's `scrollTop`, so clicking into
 * a solution from a scrolled Project Settings page dropped the user halfway
 * down the new page, and the full-page loader (which is positioned against the
 * document, see `centeredContainerStyles` in `router.tsx`) sat above the fold
 * while it loaded, reading as a blank screen.
 *
 * `NavigationType.Pop` is deliberately exempt. `history.scrollRestoration` is
 * `'auto'` and the browser DOES restore the offset of a same-document history
 * entry (verified in Chrome: scroll to 500 on A, push B, go back → 500), and
 * react-router reports the INITIAL render as `Pop` too, which covers a reload
 * of a scrolled page. Resetting unconditionally would overwrite both, so
 * pressing Back from a solution would dump the user at the top of the list
 * they came from.
 *
 * `useLayoutEffect` so the reset lands before the new route is painted — the
 * user never sees the old offset. Note this does NOT precede the newly mounted
 * route's own layout effects: React commits the child DOM and runs layout
 * effects bottom-up, so anything in the incoming route that measures the
 * document scroll during that commit still sees the old value. Nothing does
 * today.
 *
 * The dependency array is load-bearing, not an optimisation: `Root` re-renders
 * on every XState context change, so resetting on every render would yank a
 * user who had scrolled down back to the top at arbitrary moments. Both that
 * case and the Back case are pinned by tests.
 */
export const useResetScrollOnRouteChange = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (navigationType === NavigationType.Pop) return;

    window.scrollTo(0, 0);
  }, [pathname, navigationType]);
};
